'use client';

import { useCallback, useState, useEffect, useRef } from 'react';
import dynamic from 'next/dynamic';
import AdminPasswordPrompt from './AdminPasswordPrompt';
import IllustrationLayer from './IllustrationLayer';

const CustomCursor = dynamic(() => import('./CustomCursor'), { ssr: false });
const FullscreenButton = dynamic(() => import('./FullscreenButton'), { ssr: false });
const WelcomeHint = dynamic(() => import('./WelcomeHint'), { ssr: false });
const AmbientParticles = dynamic(() => import('./AmbientParticles'), { ssr: false });

interface WatercolourParticle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  radius: number;
  color: string;
  alpha: number;
  life: number;
  maxLife: number;
  blur: number;
}

const WATERCOLOUR_COLORS = [
  '232, 180, 184',
  '201, 184, 212',
  '168, 197, 160',
  '232, 213, 163',
  '242, 201, 184',
  '210, 190, 210',
  '220, 210, 195',
];

function GlitterCanvasInline() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const particlesRef = useRef<WatercolourParticle[]>([]);
  const rafRef = useRef<number>(0);
  const lastMouseRef = useRef({ x: -999, y: -999 });
  const lastSpawnRef = useRef(0);

  const spawnParticles = useCallback((x: number, y: number, speed: number) => {
    const now = performance.now();
    if (now - lastSpawnRef.current < 30) return;
    lastSpawnRef.current = now;

    const count = Math.min(1 + Math.floor(speed * 0.08), 4);

    for (let i = 0; i < count; i++) {
      const color = WATERCOLOUR_COLORS[Math.floor(Math.random() * WATERCOLOUR_COLORS.length)];
      const radius = 4 + Math.random() * 9;
      const angle = Math.random() * Math.PI * 2;
      const spd = 0.1 + Math.random() * 0.5;

      particlesRef.current.push({
        x: x + (Math.random() - 0.5) * 8,
        y: y + (Math.random() - 0.5) * 8,
        vx: Math.cos(angle) * spd,
        vy: Math.sin(angle) * spd - 0.15,
        radius,
        color,
        alpha: 0.18 + Math.random() * 0.18,
        life: 0,
        maxLife: 55 + Math.random() * 45,
        blur: 8 + Math.random() * 10,
      });
    }

    if (particlesRef.current.length > 180) {
      particlesRef.current = particlesRef.current.slice(-140);
    }
  }, []);

  const animate = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.clearRect(0, 0, canvas.width, canvas.height);
    particlesRef.current = particlesRef.current.filter(p => p.life < p.maxLife);

    for (const p of particlesRef.current) {
      p.life++;
      p.x += p.vx;
      p.y += p.vy;
      p.vx *= 0.97;
      p.vy *= 0.97;

      const progress = p.life / p.maxLife;
      const fadeAlpha = progress < 0.12
        ? progress / 0.12
        : progress > 0.5
          ? 1 - (progress - 0.5) / 0.5
          : 1;

      const currentAlpha = p.alpha * fadeAlpha;
      if (currentAlpha < 0.005) continue;

      ctx.save();
      ctx.shadowColor = `rgba(${p.color}, ${(currentAlpha * 0.6).toFixed(3)})`;
      ctx.shadowBlur = p.blur;
      ctx.globalAlpha = currentAlpha;

      const grad = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, p.radius);
      grad.addColorStop(0, `rgba(${p.color}, 1)`);
      grad.addColorStop(0.5, `rgba(${p.color}, 0.5)`);
      grad.addColorStop(1, `rgba(${p.color}, 0)`);

      ctx.fillStyle = grad;
      ctx.beginPath();
      ctx.arc(p.x, p.y, p.radius, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();
    }

    rafRef.current = requestAnimationFrame(animate);
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resize();
    window.addEventListener('resize', resize);

    const handleMouseMove = (e: MouseEvent) => {
      const { clientX: x, clientY: y } = e;
      const dx = x - lastMouseRef.current.x;
      const dy = y - lastMouseRef.current.y;
      const speed = Math.sqrt(dx * dx + dy * dy);
      lastMouseRef.current = { x, y };
      if (speed > 2) spawnParticles(x, y, speed);
    };

    window.addEventListener('mousemove', handleMouseMove);
    rafRef.current = requestAnimationFrame(animate);

    return () => {
      window.removeEventListener('resize', resize);
      window.removeEventListener('mousemove', handleMouseMove);
      cancelAnimationFrame(rafRef.current);
    };
  }, [animate, spawnParticles]);

  return (
    <canvas
      ref={canvasRef}
      id="watercolour-trail-canvas"
      aria-hidden="true"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
        zIndex: 10,
      }}
    />
  );
}

export default function OdettesComputerInteractive() {
  const [showPasswordPrompt, setShowPasswordPrompt] = useState(false);

  const handleAAADetected = useCallback(() => {
    setShowPasswordPrompt(true);
  }, []);

  const handlePasswordDismiss = useCallback(() => {
    setShowPasswordPrompt(false);
  }, []);

  return (
    <>
      <IllustrationLayer onAAADetected={handleAAADetected} />
      <AmbientParticles />
      <GlitterCanvasInline />
      <CustomCursor />
      <FullscreenButton />
      <WelcomeHint />
      {showPasswordPrompt && (
        <AdminPasswordPrompt onDismiss={handlePasswordDismiss} />
      )}
    </>
  );
}